import { BookOpen, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BOOK, COVER_SRC, openAmazon, bmTrack, BM_EVENTS } from "@/lib/businessMastery";

// Renders the approved real cover; falls back to a branded PLACEHOLDER tile until the asset exists.
function CoverTile({ className = "", compact = false }) {
  const [failed, setFailed] = useState(false);
  if (!failed) {
    return (
      <img
        data-testid="bm-cover-image"
        src={COVER_SRC}
        alt={`${BOOK.title} by ${BOOK.author} — book cover`}
        onError={() => setFailed(true)}
        className={`shrink-0 rounded-lg border border-[#D4AF37]/30 object-contain bg-[#0F0F10] ${className}`}
      />
    );
  }
  return (
    <div data-testid="bm-cover-placeholder"
      className={`shrink-0 overflow-hidden rounded-lg border border-[#D4AF37]/30 bg-gradient-to-br from-[#1C1A14] to-[#0F0F10] flex flex-col justify-between ${compact ? "p-1.5" : "p-3"} ${className}`}>
      <BookOpen className={`${compact ? "w-3 h-3" : "w-4 h-4"} text-[#D4AF37]`} />
      <div className="min-w-0">
        <div className={`font-heading text-white leading-tight ${compact ? "text-[9px]" : "text-sm"} truncate`}>{BOOK.title}</div>
        <div className={`text-[#D4AF37] ${compact ? "text-[7px]" : "text-[10px] mt-1"} truncate`}>{BOOK.author}</div>
      </div>
    </div>
  );
}

/** variant: "reading" (in-lesson recommended reading) | "featured" (Academy card) */
export function BusinessMasteryCard({ variant = "reading", source = "", course = "", lesson = "", location = "" }) {
  const navigate = useNavigate();
  const ctx = { source, course, lesson, location };
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    bmTrack(BM_EVENTS.view, { source, course, lesson, location });
  }, [source, course, lesson, location]);
  const buy = () => openAmazon({ ...ctx, cta: "buy" });
  const learnMore = () => { bmTrack(BM_EVENTS.referralClick, { ...ctx, cta: "learn_more" }); navigate("/education/business-mastery"); };

  return (
    <div data-testid={`bm-card-${variant}`}
      className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Recommended reading</div>
      <div className="flex gap-4">
        <CoverTile className="w-20 h-28" />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg text-white">{BOOK.title}</h3>
          <div className="text-sm text-[#A1A1AA] mb-1">By {BOOK.author}</div>
          <div className="text-[11px] text-[#71717A] mb-2">Available on Amazon</div>
          <p className="text-sm text-[#A1A1AA] leading-relaxed mb-3">
            {variant === "featured" ? BOOK.description : "Continue your business education with Business Mastery."}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={buy} data-testid="bm-buy-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-10 px-5 hover:opacity-90">
              Buy Business Mastery <ExternalLink className="w-4 h-4 ml-1.5" />
            </Button>
            {variant !== "page" && (
              <button onClick={learnMore} data-testid="bm-learnmore-btn" className="text-sm text-[#D4AF37] hover:underline">Learn more</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { CoverTile as BusinessMasteryCover };
